/**
 * OAuth authentication flow for Mastodon.
 * Handles app registration, authorization, and token management.
 */

import { get, post, ApiError } from "./http";
import { authStorage } from "./storage";
import type { OAuthApp, OAuthToken, Account } from "./types";

const DEFAULT_SCOPES = "read write follow push";

export interface AuthConfig {
	/** Name of your application */
	clientName: string;
	/** OAuth redirect URI - must match what's registered */
	redirectUri: string;
	/** OAuth scopes to request */
	scopes?: string;
	/** Your app's website (optional) */
	website?: string;
}

export interface AuthState {
	isAuthenticated: boolean;
	hasAppCredentials: boolean;
	instanceDomain: string | null;
	userId: string | null;
}

/**
 * Get current authentication state
 */
export function getAuthState(): AuthState {
	return {
		isAuthenticated: authStorage.isAuthenticated(),
		hasAppCredentials: authStorage.hasAppCredentials(),
		instanceDomain: authStorage.instanceDomain,
		userId: authStorage.userId,
	};
}

/**
 * Normalize instance domain to ensure it has https:// prefix
 */
export function normalizeInstanceDomain(domain: string): string {
	let normalized = domain.trim().toLowerCase();

	// Remove trailing slashes
	normalized = normalized.replace(/\/+$/, "");

	// Add https:// if no protocol specified
	if (!normalized.startsWith("http://") && !normalized.startsWith("https://")) {
		normalized = `https://${normalized}`;
	}

	return normalized;
}

/**
 * Register an OAuth application with a Mastodon instance.
 * This should be called once when the user first provides their instance domain.
 */
export async function registerApp(
	instanceDomain: string,
	config: AuthConfig,
): Promise<OAuthApp> {
	const domain = normalizeInstanceDomain(instanceDomain);

	const app = await post<OAuthApp>(
		"/api/v1/apps",
		{
			client_name: config.clientName,
			redirect_uris: config.redirectUri,
			scopes: config.scopes ?? DEFAULT_SCOPES,
			website: config.website,
		},
		{ domain, noAuth: true },
	);

	// Store credentials
	authStorage.instanceDomain = domain;
	authStorage.clientId = app.client_id;
	authStorage.clientSecret = app.client_secret;

	return app;
}

/**
 * Build the OAuth authorization URL that the user should be redirected to.
 */
export function buildAuthUrl(config: AuthConfig): string {
	const { instanceDomain, clientId } = authStorage;

	if (!instanceDomain || !clientId) {
		throw new Error("App not registered. Call registerApp first.");
	}

	const params = new URLSearchParams({
		response_type: "code",
		client_id: clientId,
		redirect_uri: config.redirectUri,
		scope: config.scopes ?? DEFAULT_SCOPES,
		force_login: "true",
	});

	return `${instanceDomain}/oauth/authorize?${params.toString()}`;
}

/**
 * Start the OAuth flow by redirecting to the authorization page.
 */
export function startAuthFlow(config: AuthConfig): void {
	window.location.href = buildAuthUrl(config);
}

/**
 * Exchange the authorization code for an access token.
 * Call this after the user is redirected back with an auth code.
 */
export async function exchangeCodeForToken(
	code: string,
	redirectUri: string,
): Promise<OAuthToken> {
	const { instanceDomain, clientId, clientSecret } = authStorage;

	if (!instanceDomain || !clientId || !clientSecret) {
		throw new Error("Missing app credentials");
	}

	// Store the auth code
	authStorage.authCode = code;

	const token = await post<OAuthToken>(
		"/oauth/token",
		{
			grant_type: "authorization_code",
			code,
			client_id: clientId,
			client_secret: clientSecret,
			redirect_uri: redirectUri,
		},
		{ noAuth: true },
	);

	// Store the access token
	authStorage.accessToken = token.access_token;

	return token;
}

/**
 * Handle the OAuth redirect - extract code from URL and exchange for token.
 * Returns the token if successful.
 */
export async function handleAuthRedirect(
	redirectUri: string,
	urlOrSearch: string = window.location.search,
): Promise<OAuthToken> {
	const params = new URLSearchParams(urlOrSearch);
	const code = params.get("code");
	const error = params.get("error");
	const errorDescription = params.get("error_description");

	if (error) {
		throw new ApiError(errorDescription ?? error, 0);
	}

	if (!code) {
		throw new ApiError("No authorization code in redirect URL", 0);
	}

	return exchangeCodeForToken(code, redirectUri);
}

/**
 * Verify credentials and get the current user's account.
 * Also stores the user ID.
 */
export async function verifyCredentials(): Promise<Account> {
	const account = await get<Account>("/api/v1/accounts/verify_credentials");
	authStorage.userId = account.id;
	return account;
}

/**
 * Revoke the current access token and clear stored credentials.
 */
export async function logout(): Promise<void> {
	const { clientId, clientSecret, accessToken } = authStorage;

	if (clientId && clientSecret && accessToken) {
		try {
			await post(
				"/oauth/revoke",
				{
					client_id: clientId,
					client_secret: clientSecret,
					token: accessToken,
				},
				{ noAuth: true },
			);
		} catch {
			// Ignore errors during revoke - we'll clear local storage anyway
		}
	}

	authStorage.clear();
}

/**
 * Complete authentication flow helper.
 * Combines app registration, auth URL generation, and token exchange.
 */
export const auth = {
	/** Get current auth state */
	getState: getAuthState,

	/** Register OAuth app with instance */
	registerApp,

	/** Build authorization URL */
	buildAuthUrl,

	/** Redirect to authorization page */
	startAuthFlow,

	/** Exchange auth code for token */
	exchangeCode: exchangeCodeForToken,

	/** Handle OAuth redirect */
	handleRedirect: handleAuthRedirect,

	/** Verify credentials and get current user */
	verifyCredentials,

	/** Logout and revoke token */
	logout,

	/** Check if authenticated */
	isAuthenticated: () => authStorage.isAuthenticated(),

	/** Storage access */
	storage: authStorage,
};
